import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import { cn } from '@core/utils/cn';
import { Spinner, UserText } from '@shared/components';
import { formatDate } from '@core/utils/formatters';
import {
  useMarkRead,
  useNotifications,
  useUnreadCount,
} from '@features/notifications/services/notifications.service';
import type { NotificationItem } from '@features/notifications/types/notification.types';

/** Past this the badge shows "99+". The server stops counting at 100 for the
 *  same reason, so the two agree about when the number stops being exact. */
const BADGE_CEILING = 99;

function destinationOf(item: NotificationItem): string | null {
  if (item.project) return `/projects/${String(item.project.id)}`;
  return null;
}

export function NotificationBell(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { data: badge } = useUnreadCount();
  const { data: items, isPending } = useNotifications(open);
  const markRead = useMarkRead();

  // Close on an outside click or Escape. A popover that only closes via its own
  // button strands keyboard users and reads as broken on touch.
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event: MouseEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const unread = badge?.value ?? 0;
  const label = unread > BADGE_CEILING || badge?.capped ? `${String(BADGE_CEILING)}+` : String(unread);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen((wasOpen) => !wasOpen);
        }}
        aria-expanded={open}
        aria-label={t('notifications.open')}
        data-testid="notification-bell"
        className="relative rounded-full border border-line p-2 text-muted transition hover:border-muted hover:text-text dda-focus"
      >
        <Bell size={18} aria-hidden="true" />
        {unread > 0 ? (
          <span
            data-testid="notification-badge"
            className="dda-figure absolute -end-1 -top-1 min-w-[18px] rounded-full bg-accent px-1 text-[10px] font-bold leading-[18px] text-accent-ink"
          >
            {label}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label={t('notifications.title')}
          // `bg-card`, not `bg-surface`: there is no `surface` colour in this
          // theme, so that class emitted nothing and the panel rendered
          // transparent — the page's own heading read straight through the list.
          // A missing Tailwind colour fails silently, which is why it survived.
          className="absolute end-0 z-50 mt-2 w-80 rounded-2xl border border-card-bd bg-card p-2 shadow-e3"
        >
          <div className="flex items-center justify-between px-2 py-1">
            <span className="text-sm font-semibold">{t('notifications.title')}</span>
            {unread > 0 ? (
              <button
                type="button"
                className="text-xs text-accent hover:underline"
                onClick={() => {
                  markRead.mutate({ all: true });
                }}
              >
                {t('notifications.markAllRead')}
              </button>
            ) : null}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {isPending ? (
              <div className="p-4">
                <Spinner />
              </div>
            ) : !items || items.length === 0 ? (
              <p className="p-4 text-sm text-muted">{t('notifications.empty')}</p>
            ) : (
              <ul>
                {items.map((item) => {
                  const to = destinationOf(item);
                  const body = (
                    <div
                      className={cn(
                        'rounded-xl px-2 py-2 text-sm transition hover:bg-[var(--btn-ghost-hover)]',
                        item.read_at ? 'text-muted' : 'font-medium text-text',
                      )}
                    >
                      <UserText>
                        {t(`notifications.kind.${item.kind}`, {
                          project: item.project?.name ?? '',
                        })}
                      </UserText>
                      <div className="dda-figure pt-0.5 text-xs text-muted">
                        {formatDate(item.created_at, i18n.language)}
                      </div>
                    </div>
                  );
                  return (
                    <li key={item.id}>
                      {to ? (
                        <Link
                          to={to}
                          onClick={() => {
                            setOpen(false);
                            if (!item.read_at) markRead.mutate({ ids: [item.id] });
                          }}
                        >
                          {body}
                        </Link>
                      ) : (
                        body
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
